import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBacktestStore } from "../stores/backtestStore";
import { useAuthStore } from "../stores/authStore";
import { isAdmin } from "../services/authService";
import { runBacktest } from "../services/api";
import {
  getCommunityStrategy,
  bookmarkStrategy,
  unbookmarkStrategy,
  investInStrategy,
  logBacktestRun,
} from "../services/strategyService";
import { useBuilderStore } from "../stores/builderStore";
import { EquityChart } from "../components/charts/EquityChart";
import type { DateRange } from "../components/charts/EquityChart";
import { MetricsGrid } from "../components/MetricsGrid";
import { TodayActivity } from "../components/TodayActivity";
import { RangeAnalysisPanel } from "../components/RangeAnalysisPanel";
import { HistoricalAllocations } from "../components/HistoricalAllocations";
import { PageHeader } from "../components/PageHeader";
import { SaveStrategyModal } from "../components/SaveStrategyModal";
import { ShareStrategyModal } from "../components/ShareStrategyModal";
import { InvestModal } from "../components/InvestModal";
import { LoginPromptModal } from "../components/LoginPromptModal";
import { AboutStrategy } from "../components/AboutStrategy";
import OptimizationConfigModal from "../components/optimizer/OptimizationConfigModal";
import { FEATURES } from "../featureFlags";
import { MonteCarloAnalysis } from "../components/MonteCarloAnalysis";
import { runMonteCarlo } from "../utils/monteCarlo";
import type { MonteCarloResult } from "../utils/monteCarlo";
import type { StrategyMetrics } from "../services/strategyService";

const SAMPLE_STRATEGY = `strategy "TQQQ Falling Knife":
  asset_class: EQUITIES
  rebalance: threshold(10)

  root:
    if QQQ.return(5) < -5.5:
      group "Volatile Market":
        weights:
          - 20%:
            group "Conditional VXX":
              if UVXY.rsi(21) > 62.0:
                if UVXY.rsi(10) > 74.0:
                  if UVXY.rsi(10) < 84.0:
                    if UVXY.return(2) < 4.5:
                      FLOT
                    else:
                      equal:
                        -
                          FLOT
                        -
                          FLOT
                        -
                          VXX
                        -
                          VXX
                        -
                          FLOT
                        -
                          FLOT
                  else:
                    FLOT
                else:
                  FLOT
              else:
                weights:
                  - 100%:
                    BIL
          - 80%:
            BIL
    else:
      group "Core or BIL":
        if SPY.return(5) < -5.5:
          BIL
        else:
          group "Core":
            if TQQQ.rsi(10) > 79.0:
              UVXY
            else:
              if SPXL.rsi(10) > 80.0:
                UVXY
              else:
                if QQQ.rsi(10) < 31.0:
                  equal:
                    -
                      TQQQ
                    -
                      TECL
                else:
                  equal:
                    -
                      GLD
                    -
                      SHY
                    -
                      UUP`;

// Time period options with days
const TIME_PERIODS = [
  { label: "1M", days: 21 },
  { label: "3M", days: 63 },
  { label: "6M", days: 126 },
  { label: "1Y", days: 252 },
  { label: "3Y", days: 756 },
  { label: "5Y", days: 1260 },
  { label: "7Y", days: 1764 },
  { label: "10Y", days: 2520 },
];

// Progress stages for the backtest
const PROGRESS_STAGES = [
  { id: "parsing", label: "Parsing strategy", icon: "📝" },
  { id: "fetching", label: "Fetching market data", icon: "📊" },
  { id: "running", label: "Running simulation", icon: "⚙️" },
  { id: "calculating", label: "Calculating metrics", icon: "📈" },
  { id: "complete", label: "Complete", icon: "✅" },
];

interface ProgressState {
  stage: string;
  message: string;
  progress: number;
  startTime: number;
}

export function BacktestPage() {
  const { strategyId } = useParams<{ strategyId?: string }>();
  const navigate = useNavigate();

  const {
    result,
    isLoading,
    error,
    timeRange,
    strategyCode,
    setResult,
    setLoading,
    setError,
    setTimeRange,
    setStrategyCode,
  } = useBacktestStore();

  const [inputMode, setInputMode] = useState<"strategy" | "build">(
    strategyId ? "strategy" : "build"
  );
  const [showActivity, setShowActivity] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [strategyName, setStrategyName] = useState<string>("");
  const [strategyThreshold, setStrategyThreshold] = useState<number | null>(null);
  const [strategyMetrics, setStrategyMetrics] = useState<StrategyMetrics | null>(null);
  const [currentStrategyId, setCurrentStrategyId] = useState<string | null>(
    null,
  );
  const [strategyNotFound, setStrategyNotFound] = useState(false);
  const [days, setDays] = useState(252);
  const [selectedPeriod, setSelectedPeriod] = useState("1Y");
  const [useCache, setUseCache] = useState(true);
  const [initialCapital, setInitialCapital] = useState(100000);
  const [slippageBps, setSlippageBps] = useState(1);
  const [includeRegulatoryFees, setIncludeRegulatoryFees] = useState(true);
  const [wholeSharesOnly, setWholeSharesOnly] = useState(false);
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const [dateMode, setDateMode] = useState<"period" | "custom">("period");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedRange, setSelectedRange] = useState<DateRange | null>(null);

  // Bookmark & Invest state
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isCommunityStrategy, setIsCommunityStrategy] = useState(false);
  const [isBookmarkLoading, setIsBookmarkLoading] = useState(false);
  const [showInvestModal, setShowInvestModal] = useState(false);
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [monteCarloResult, setMonteCarloResult] = useState<MonteCarloResult | null>(null);
  const [isRunningMC, setIsRunningMC] = useState(false);
  const [loginPromptAction, setLoginPromptAction] = useState<
    "bookmark" | "invest" | "save" | "optimize" | null
  >(null);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const userIsAdmin = isAdmin(user);

  // WebSocket reference for progress updates
  const wsRef = useRef<WebSocket | null>(null);
  const backtestIdRef = useRef<string | null>(null);

  // Load strategy from URL param
  useEffect(() => {
    if (strategyId) {
      setStrategyNotFound(false);
      setIsBookmarked(false);
      setInputMode("strategy"); // Community/bookmarked strategy mode
      // Try to load as user's own strategy first (includes private, only if authenticated), fallback to community
      const loadStrategy = async () => {
        // Only try user's own strategy if authenticated to avoid triggering session expired modal
        if (isAuthenticated) {
          try {
            const { getStrategy } = await import("../services/strategyService");
            const strategy = await getStrategy(strategyId);
            setStrategyCode(strategy.code || "");
            setStrategyName(strategy.name);
            setStrategyThreshold(strategy.threshold_pct ?? null);
            setStrategyMetrics(strategy.metrics || null);
            setCurrentStrategyId(strategy.id);
            setIsBookmarked(strategy.is_bookmarked || false);
            setIsCommunityStrategy(strategy.is_community || false);
            return; // Success - don't try community
          } catch {
            // Fall through to community strategy
          }
        }
        
        // Try community strategy (works for both authenticated and unauthenticated users)
        try {
          const strategy = await getCommunityStrategy(strategyId);
          setStrategyCode(strategy.code || "");
          setStrategyName(strategy.name);
          setStrategyThreshold(strategy.threshold_pct ?? null);
          setStrategyMetrics(strategy.metrics || null);
          setCurrentStrategyId(strategy.id);
          setIsBookmarked(strategy.is_bookmarked || false);
          setIsCommunityStrategy(true);
        } catch {
          setStrategyNotFound(true);
        }
      };
      
      loadStrategy();
    } else {
      setCurrentStrategyId(null);
      setStrategyName("");
      setStrategyThreshold(null);
      setStrategyMetrics(null);
      setIsBookmarked(false);
      setIsCommunityStrategy(false);
      // Don't change inputMode when navigating away - keep user's choice
    }
  }, [strategyId, setStrategyCode, isAuthenticated]);

  // State for strategy mode

  // Track previous strategy code to detect changes
  const prevStrategyCodeRef = useRef(strategyCode);

  // Clear results when strategy code changes
  useEffect(() => {
    if (prevStrategyCodeRef.current !== strategyCode && result) {
      // Strategy changed, clear old results
      setResult(null);
    }
    prevStrategyCodeRef.current = strategyCode;
  }, [strategyCode, result, setResult]);

  // Cleanup WebSocket and reset store on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      // Clear backtest results when leaving the page to avoid stale data
      setResult(null);
    };
  }, [setResult]);

  // Reset results when strategyId changes (loading a different strategy)
  useEffect(() => {
    setResult(null);
  }, [strategyId, setResult]);

  const connectProgressWebSocket = useCallback(() => {
    // WebSocket progress updates are optional - API runs synchronously
    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Note: WebSocket endpoint not currently implemented on API
    // Progress is shown via estimated time based on period length
  }, []);

  const handlePeriodClick = (period: { label: string; days: number }) => {
    setSelectedPeriod(period.label);
    setDays(period.days);
  };

  const handleRunBacktest = async () => {
    // Validate based on mode
    if (inputMode === "build" && !strategyCode.trim()) {
      setError("Please enter strategy code");
      return;
    }

    // Non-admin users: block if strategy code is not TC DSL format
    if (inputMode === "build" && !userIsAdmin) {
      const trimmed = strategyCode.trim();
      const isTcDsl = /^strategy\s+"/.test(trimmed);
      if (!isTcDsl) {
        setError("Format is not recognized. Please provide a valid TradeCraft strategy definition then retry.");
        return;
      }
    }

    if (inputMode === "strategy" && !currentStrategyId) {
      setError("No strategy selected");
      return;
    }

    // Validate custom dates if in custom mode
    if (dateMode === "custom") {
      if (!startDate || !endDate) {
        setError("Please select both start and end dates");
        return;
      }
      if (new Date(startDate) >= new Date(endDate)) {
        setError("Start date must be before end date");
        return;
      }
    }

    // Clear previous results immediately
    setResult(null);
    setSelectedRange(null);
    setLoading(true);
    setError(null);

    // Initialize progress
    const backtestId = `bt_${Date.now()}`;
    backtestIdRef.current = backtestId;
    setProgress({
      stage: "parsing",
      message:
        inputMode === "strategy"
          ? "Loading strategy..."
          : "Initializing backtest...",
      progress: 0,
      startTime: Date.now(),
    });

    // Try to connect WebSocket for real-time progress
    connectProgressWebSocket();

    try {
      // Simulate progress stages while waiting for actual backtest
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (!prev) return prev;
          const elapsed = Date.now() - prev.startTime;

          let newProgress = Math.min(90, (elapsed / 30000) * 100);
          let stage = "running";
          let message = "Running simulation...";

          if (elapsed < 2000) {
            stage = "parsing";
            message = "Parsing strategy definition...";
            newProgress = Math.min(15, (elapsed / 2000) * 15);
          } else if (elapsed < 8000) {
            stage = "fetching";
            message = "Fetching market data...";
            newProgress = 15 + Math.min(35, ((elapsed - 2000) / 6000) * 35);
          } else if (elapsed < 25000) {
            stage = "running";
            message = "Running backtest simulation...";
            newProgress = 50 + Math.min(35, ((elapsed - 8000) / 17000) * 35);
          } else {
            stage = "calculating";
            message = "Calculating performance metrics...";
            newProgress = 85 + Math.min(5, ((elapsed - 25000) / 5000) * 5);
          }

          return { ...prev, stage, message, progress: newProgress };
        });
      }, 500);

      // Build request based on mode:
      // - strategy: send strategyId only (code fetched from DB)
      // - build: send strategyCode
      const response = await runBacktest(
        inputMode === "build" ? strategyCode : "", // Only send code in build mode
        days,
        {
          useCache,
          useFastMode: true,
          strategyId: inputMode === "strategy" ? currentStrategyId ?? undefined : undefined,
          startDate: dateMode === "custom" ? startDate : undefined,
          endDate: dateMode === "custom" ? endDate : undefined,
          initialCapital,
          slippageBps,
          includeRegulatoryFees,
          wholeSharesOnly,
        },
      );

      clearInterval(progressInterval);

      setProgress({
        stage: "complete",
        message: "Backtest complete!",
        progress: 100,
        startTime: Date.now(),
      });

      setResult(response);

      // Log backtest run to activity log (only for authenticated users - fire and forget)
      if (isAuthenticated) {
        const backtestDuration = progress?.startTime ? Date.now() - progress.startTime : 0;
        logBacktestRun({
          strategy_id: currentStrategyId ?? undefined,
          strategy_name: strategyName || undefined,
          days,
          cagr: response.metrics?.cagr,
          sharpe: response.metrics?.sharpe,
          max_drawdown: response.metrics?.maxDrawdown,
          duration_ms: backtestDuration,
        }).catch((err) => {
          // Don't surface errors from logging to user
          console.warn("[BacktestPage] Failed to log backtest:", err);
        });
      }

      // Clear progress after a short delay
      setTimeout(() => {
        setProgress(null);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Backtest failed");
      setProgress(null);
    } finally {
      setLoading(false);
      if (wsRef.current) {
        wsRef.current.close();
      }
    }
  };

  const handleLoadSample = () => {
    setStrategyCode(SAMPLE_STRATEGY);
    setInputMode("build");
    setCurrentStrategyId(null);
    setStrategyName("");
    // Navigate to base backtest URL if viewing a strategy
    if (strategyId) {
      navigate("/backtest");
    }
  };

  const handleToggleBookmark = async () => {
    if (!strategyId || isBookmarkLoading) return;

    if (!isAuthenticated) {
      setLoginPromptAction("bookmark");
      return;
    }

    setIsBookmarkLoading(true);
    try {
      if (isBookmarked) {
        await unbookmarkStrategy(strategyId);
        setIsBookmarked(false);
      } else {
        const resp = await bookmarkStrategy(strategyId);
        setIsBookmarked(true);
        // If a copy was created, navigate to the user's copy
        if (resp.strategy_copy) {
          setCurrentStrategyId(resp.strategy_copy.id);
          setStrategyName(resp.strategy_copy.name);
          navigate(`/backtest/${resp.strategy_copy.id}`, { replace: true });
        }
      }
    } catch (err) {
      console.error("Failed to toggle bookmark:", err);
      setError(
        err instanceof Error ? err.message : "Failed to update bookmark",
      );
    } finally {
      setIsBookmarkLoading(false);
    }
  };

  const handleInvest = async (amount: number, brokerageAccountId: string) => {
    if (!strategyId) return;

    await investInStrategy(strategyId, amount, brokerageAccountId);
    // Navigate to portfolio after successful investment
    navigate("/portfolio", {
      state: {
        message: `Successfully invested $${amount.toLocaleString()} in ${strategyName}`,
      },
    });
  };

  const getStageIndex = (stageId: string) => {
    return PROGRESS_STAGES.findIndex((s) => s.id === stageId);
  };

  // Strategy not found error
  if (strategyNotFound) {
    return (
      <div>
        <PageHeader
          title="Strategy Not Found"
          subtitle="The requested strategy could not be found"
          backgroundImage="https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=1920&q=80"
        />
        <div className="px-4 sm:px-6 lg:px-8 pb-8 max-w-7xl mx-auto">
          <div className="glass-card p-8 text-center">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-2xl font-bold text-surface-100 mb-2">
              Strategy Not Found
            </h2>
            <p className="text-surface-400 mb-6">
              The strategy with ID "{strategyId}" doesn't exist or may have been
              removed.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate("/search")}
                className="btn-primary px-6 py-3"
              >
                Community Strategies
              </button>
              <button
                onClick={() => navigate("/backtest")}
                className="btn-secondary px-6 py-3"
              >
                Create New Backtest
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header with trading background */}
      <PageHeader
        title={strategyName || "Backtest"}
        subtitle={
          strategyName
            ? `Testing: ${strategyName}`
            : "Test your trading strategy against historical data"
        }
        backgroundImage="https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8 max-w-7xl mx-auto">
        {/* Action Buttons for Community Strategies */}
        {strategyId && strategyName && (
          <div className="glass-card p-4 mb-6 flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-2 ml-auto">
              {/* Bookmark Button */}
              <button
                onClick={handleToggleBookmark}
                disabled={isBookmarkLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  isBookmarked
                    ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/50 hover:bg-yellow-500/30"
                    : "bg-surface-700 text-surface-300 hover:bg-surface-600 border border-surface-600"
                }`}
              >
                {isBookmarkLoading ? (
                  <span className="spinner w-4 h-4" />
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill={isBookmarked ? "currentColor" : "none"}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                    />
                  </svg>
                )}
                {isBookmarked ? "In Watchlist" : "Add to Watchlist"}
              </button>

              {/* Invest Button */}
              <button
                onClick={() => {
                  if (!isAuthenticated) {
                    setLoginPromptAction("invest");
                    return;
                  }
                  setShowInvestModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-500 text-white hover:bg-green-600 transition-all duration-200 shadow-lg shadow-green-500/25"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Invest
              </button>


            </div>
          </div>
        )}

        {/* About This Strategy — community strategies only */}
        {isCommunityStrategy && strategyName && (
          <AboutStrategy
            strategyName={strategyName}
            metrics={result?.metrics || strategyMetrics || undefined}
          />
        )}

        {/* Input Section */}
        <div className="glass-card p-4 sm:p-6 mb-6">
          {/* Mode Selection Tabs */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4">
            {/* Only show Strategy tab if viewing a community/bookmarked strategy */}
            {strategyId && (
              <button
                onClick={() => setInputMode("strategy")}
                className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 ${
                  inputMode === "strategy"
                    ? "bg-primary-500 text-white shadow-lg shadow-primary-500/25"
                    : "bg-surface-800 text-surface-400 hover:bg-surface-700 border border-surface-700/50"
                }`}
              >
                📊 View Strategy
              </button>
            )}
            <button
              onClick={() => {
                setInputMode("build");
                if (strategyId) {
                  // Clear strategy context when switching to build mode
                  setCurrentStrategyId(null);
                  setStrategyName("");
                  setStrategyCode("");
                  navigate("/backtest");
                }
              }}
              className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 ${
                inputMode === "build"
                  ? "bg-primary-500 text-white shadow-lg shadow-primary-500/25"
                  : "bg-surface-800 text-surface-400 hover:bg-surface-700 border border-surface-700/50"
              }`}
            >
              🛠️ Build Strategy
            </button>
            <button
              onClick={() => navigate("/builder")}
              className="text-sm font-medium px-4 py-2 rounded-lg bg-surface-800 text-surface-400 hover:bg-surface-700 border border-surface-700/50 transition-all duration-200 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Visual Builder
            </button>
            <button
              onClick={handleLoadSample}
              className="text-sm font-medium px-4 py-2 rounded-lg bg-surface-800 text-surface-400 hover:bg-surface-700 border border-surface-700/50 sm:ml-auto transition-all duration-200 w-full sm:w-auto"
            >
              Load Sample Strategy
            </button>
          </div>

          {/* Strategy Mode - Show strategy info card */}
          {inputMode === "strategy" && currentStrategyId && (
            <div className="bg-surface-800/50 rounded-lg p-4 border border-surface-700/50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-surface-100">{strategyName}</h3>
                    <p className="text-sm text-surface-400">
                      Community Strategy
                      {strategyThreshold !== null && (
                        <span className="ml-2 text-primary-400">
                          • {strategyThreshold === 0 ? 'Daily rebalance' : `${(strategyThreshold * 100).toFixed(0)}% threshold`}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                {isAuthenticated && (
                <div className="flex items-center gap-2">
                  {/* Edit in Builder */}
                  <button
                    onClick={() => {
                      const sourceType = isCommunityStrategy ? 'community' : 'user';
                      useBuilderStore.getState().loadFromDSL(
                        strategyCode,
                        sourceType,
                        currentStrategyId,
                        strategyName,
                      );
                      navigate("/builder");
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-primary-500/20 text-primary-400 hover:bg-primary-500/30 border border-primary-500/30 transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit in Builder
                  </button>
                </div>
                )}
              </div>
            </div>
          )}

          {/* Build Mode - Code editor + Visual Builder CTA */}
          {inputMode === "build" && (
            <div className="space-y-3">
              {/* Visual Builder CTA */}
              {isAuthenticated && (
              <div className="bg-surface-800/50 rounded-lg p-4 border border-primary-500/20 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-surface-100">Visual Strategy Builder</h4>
                    <p className="text-xs text-surface-400">Build strategies with drag-and-drop tree editor, live DSL dry run, and integrated backtesting</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/builder")}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-all shadow-lg shadow-primary-500/20 whitespace-nowrap"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Open Builder
                </button>
              </div>
              )}
              {/* Code textarea (still available for paste-and-test) */}
              <div className="relative group">
                <textarea
                  value={strategyCode}
                  onChange={(e) => setStrategyCode(e.target.value)}
                  placeholder="Paste your TradeCraft strategy code here..."
                  className="input-field w-full h-40 sm:h-48 font-mono text-sm resize-none"
                />
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <div className="bg-surface-900 text-surface-300 text-xs px-3 py-1.5 rounded-lg border border-surface-700 shadow-lg">
                    Please provide your TradeCraft strategy definition in this box.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Backtest Options */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 mt-4 pt-4 border-t border-surface-700/50">
            {/* Date Mode Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDateMode("period")}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  dateMode === "period"
                    ? "bg-primary-500 text-white shadow-lg shadow-primary-500/25"
                    : "bg-surface-800 text-surface-400 hover:bg-surface-700 border border-surface-700/50"
                }`}
              >
                Period
              </button>
              <button
                onClick={() => setDateMode("custom")}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  dateMode === "custom"
                    ? "bg-primary-500 text-white shadow-lg shadow-primary-500/25"
                    : "bg-surface-800 text-surface-400 hover:bg-surface-700 border border-surface-700/50"
                }`}
              >
                Custom Dates
              </button>
            </div>

            <div className="hidden sm:block h-6 w-px bg-surface-700" />

            {/* Period Buttons or Date Pickers */}
            {dateMode === "period" ? (
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mb-1">
                  {TIME_PERIODS.map((period) => (
                    <button
                      key={period.label}
                      onClick={() => handlePeriodClick(period)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                        selectedPeriod === period.label
                          ? "bg-primary-500 text-white shadow-lg shadow-primary-500/25"
                          : "bg-surface-800 text-surface-400 hover:bg-surface-700 hover:text-surface-200 border border-surface-700/50"
                      }`}
                    >
                      {period.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="flex items-center gap-2">
                  <label className="text-surface-400 text-sm">Start:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="input-field py-1.5 px-3 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-surface-400 text-sm">End:</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    max={new Date().toISOString().split("T")[0]}
                    className="input-field py-1.5 px-3 text-sm"
                  />
                </div>
              </div>
            )}

            <div className="hidden sm:block h-6 w-px bg-surface-700" />

            <div className="flex items-center gap-2">
              <label className="text-surface-400 text-sm whitespace-nowrap">
                Initial Capital:
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500">
                  $
                </span>
                <input
                  type="number"
                  value={initialCapital}
                  onChange={(e) =>
                    setInitialCapital(Number(e.target.value) || 100000)
                  }
                  min={1000}
                  step={1000}
                  className="input-field py-1.5 pl-7 pr-3 text-sm w-32"
                />
              </div>
            </div>

            <div className="hidden sm:block h-6 w-px bg-surface-700" />

            <div className="flex items-center gap-2">
              <label className="text-surface-400 text-sm whitespace-nowrap">
                Slippage:
              </label>
              <div className="relative flex items-center gap-1">
                <input
                  type="number"
                  value={slippageBps}
                  onChange={(e) => setSlippageBps(Number(e.target.value) || 0)}
                  min={0}
                  max={100}
                  step={1}
                  className="input-field py-1.5 px-3 text-sm w-16"
                />
                <span className="text-surface-500 text-sm">bps</span>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={includeRegulatoryFees}
                onChange={(e) => setIncludeRegulatoryFees(e.target.checked)}
                className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-surface-400 text-sm group-hover:text-surface-300 transition-colors">
                Regulatory Fees
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer group" title="Floor all trades to whole shares (simulates non-fractional brokers like Schwab)">
              <input
                type="checkbox"
                checked={wholeSharesOnly}
                onChange={(e) => setWholeSharesOnly(e.target.checked)}
                className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-surface-400 text-sm group-hover:text-surface-300 transition-colors">
                Whole Shares
              </span>
            </label>

            <div className="hidden sm:block h-6 w-px bg-surface-700" />

            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={useCache}
                onChange={(e) => setUseCache(e.target.checked)}
                className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-surface-400 text-sm group-hover:text-surface-300 transition-colors">
                Use Cached Strategy
              </span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
            <div className="text-surface-500 text-sm">
              {inputMode === "strategy" && currentStrategyId && (
                <span className="text-green-400">
                  Ready to run backtest (fast mode)
                </span>
              )}
            </div>
            <button
              onClick={handleRunBacktest}
              disabled={
                isLoading ||
                (inputMode === "build" && !strategyCode.trim()) ||
                (inputMode === "strategy" && !currentStrategyId)
              }
              className="btn-primary px-8 py-2.5 flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              {isLoading ? (
                <>
                  <span className="spinner" />
                  Running...
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Run Backtest
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="mt-4 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}
        </div>

        {/* Progress Panel */}
        {progress && (
          <div className="glass-card p-6 mb-6 animate-slide-up">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-surface-100">
                Backtest Progress
              </h3>
              <span className="text-sm text-surface-400">
                {Math.round(progress.progress)}%
              </span>
            </div>

            {/* Progress bar */}
            <div className="progress-bar mb-6">
              <div
                className="progress-bar-fill"
                style={{ width: `${progress.progress}%` }}
              />
            </div>

            {/* Progress stages */}
            <div className="flex items-center justify-between">
              {PROGRESS_STAGES.map((stage, index) => {
                const currentIndex = getStageIndex(progress.stage);
                const isComplete = index < currentIndex;
                const isCurrent = index === currentIndex;
                const isPending = index > currentIndex;

                return (
                  <div
                    key={stage.id}
                    className="flex flex-col items-center flex-1"
                  >
                    <div
                      className={`
                    w-10 h-10 rounded-full flex items-center justify-center text-lg mb-2 transition-all duration-300
                    ${isComplete ? "bg-accent-500/20 text-accent-400" : ""}
                    ${isCurrent ? "bg-primary-500/20 text-primary-400 animate-pulse-slow ring-2 ring-primary-500/50" : ""}
                    ${isPending ? "bg-surface-800 text-surface-500" : ""}
                  `}
                    >
                      {stage.icon}
                    </div>
                    <span
                      className={`text-xs text-center transition-colors ${
                        isCurrent
                          ? "text-primary-400 font-medium"
                          : isComplete
                            ? "text-accent-400"
                            : "text-surface-500"
                      }`}
                    >
                      {stage.label}
                    </span>
                    {index < PROGRESS_STAGES.length - 1 && (
                      <div
                        className={`absolute top-5 left-1/2 w-full h-0.5 -z-10 ${
                          isComplete ? "bg-accent-500/50" : "bg-surface-700"
                        }`}
                        style={{ display: "none" }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Current message */}
            <div className="mt-4 pt-4 border-t border-surface-700/50 text-center">
              <p className="text-surface-300 text-sm">{progress.message}</p>
            </div>
          </div>
        )}

        {/* Results Section */}
        {result && (
          <div className="animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <h2 className="text-xl font-semibold text-surface-100">
                {result.strategyName}
              </h2>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <span className="text-surface-400 text-xs sm:text-sm">
                  {result.startDate} to {result.endDate}
                </span>
                <button
                  onClick={() => {
                    if (!isAuthenticated) {
                      setLoginPromptAction("save");
                      return;
                    }
                    setShowSaveModal(true);
                  }}
                  className="btn-primary flex items-center gap-2 w-full sm:w-auto"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                    />
                  </svg>
                  Save to Community
                </button>
                {currentStrategyId ? (
                  <>
                    <button
                      onClick={handleToggleBookmark}
                      disabled={isBookmarkLoading}
                      className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all w-full sm:w-auto ${
                        isBookmarked
                          ? "bg-primary-500/20 text-primary-400 border border-primary-500/30"
                          : "bg-surface-800/50 text-surface-300 border border-surface-700/50 hover:bg-surface-700/50 hover:text-surface-100"
                      }`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill={isBookmarked ? "currentColor" : "none"}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                        />
                      </svg>
                      {isBookmarked ? "In Watchlist" : "Add to Watchlist"}
                    </button>
                    <button
                      onClick={() => setShowShareModal(true)}
                      className="btn-secondary flex items-center gap-2 w-full sm:w-auto"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                        />
                      </svg>
                      Share
                    </button>
                  </>
                ) : (
                  <button
                    onClick={async () => {
                      if (!isAuthenticated) {
                        setLoginPromptAction("bookmark");
                        return;
                      }
                      setIsBookmarkLoading(true);
                      try {
                        // Create private strategy and bookmark it
                        const { createStrategy, bookmarkStrategy } = await import("../services/strategyService");
                        
                        // Try to create with original name, if it fails due to duplicate, add timestamp
                        let strategyName = result.strategyName;
                        let newStrategy;
                        
                        try {
                          newStrategy = await createStrategy({
                            name: strategyName,
                            code: result.strategyCode || strategyCode,
                            visibility: "private",
                            description: `Private strategy - ${result.metrics.cagr.toFixed(1)}% CAGR, ${result.metrics.sharpe.toFixed(2)} Sharpe`,
                            metrics: result.metrics,
                          });
                        } catch (firstErr) {
                          // If name exists, append timestamp and retry
                          if (firstErr instanceof Error && firstErr.message.includes("already exists")) {
                            strategyName = `${result.strategyName} (${new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })})`;
                            newStrategy = await createStrategy({
                              name: strategyName,
                              code: result.strategyCode || strategyCode,
                              visibility: "private",
                              description: `Private strategy - ${result.metrics.cagr.toFixed(1)}% CAGR, ${result.metrics.sharpe.toFixed(2)} Sharpe`,
                              metrics: result.metrics,
                            });
                          } else {
                            throw firstErr;
                          }
                        }
                        
                        await bookmarkStrategy(newStrategy.id);
                        setCurrentStrategyId(newStrategy.id);
                        setStrategyName(strategyName);
                        setIsBookmarked(true);
                        navigate(`/backtest/${newStrategy.id}`, { replace: true });
                      } catch (err) {
                        console.error("Failed to add to watchlist:", err);
                        setError(err instanceof Error ? err.message : "Failed to add to watchlist");
                      } finally {
                        setIsBookmarkLoading(false);
                      }
                    }}
                    disabled={isBookmarkLoading}
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all w-full sm:w-auto bg-surface-800/50 text-surface-300 border border-surface-700/50 hover:bg-surface-700/50 hover:text-surface-100"
                  >
                    {isBookmarkLoading ? (
                      <span className="spinner w-4 h-4" />
                    ) : (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                        />
                      </svg>
                    )}
                    Add to Watchlist
                  </button>
                )}
                {FEATURES.OPTIMIZER && (
                <button
                  onClick={() => {
                    if (!isAuthenticated) {
                      setLoginPromptAction("optimize");
                      return;
                    }
                    setShowOptimizeModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary-500/20 text-primary-400 border border-primary-500/30 hover:bg-primary-500/30 transition-all duration-200 whitespace-nowrap w-full sm:w-auto"
                  title="Optimize this strategy with AI"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                  Optimize
                </button>
                )}
                <button
                  onClick={() => {
                    if (!result || isRunningMC) return;
                    setIsRunningMC(true);
                    // Use requestAnimationFrame to let the UI update before heavy compute
                    requestAnimationFrame(() => {
                      const dates = result.equity.map((p) => p.date);
                      const values = result.equity.map((p) => p.value);
                      const mcResult = runMonteCarlo(dates, values);
                      setMonteCarloResult(mcResult);
                      setIsRunningMC(false);
                    });
                  }}
                  disabled={isRunningMC}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-200 whitespace-nowrap w-full sm:w-auto ${
                    monteCarloResult
                      ? "bg-violet-500/20 text-violet-400 border-violet-500/30 hover:bg-violet-500/30"
                      : "bg-surface-800/50 text-surface-300 border-surface-700/50 hover:bg-surface-700/50"
                  }`}
                  title="Run Monte Carlo simulation on equity curve"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  {isRunningMC ? "Running..." : monteCarloResult ? "Monte Carlo ✓" : "Monte Carlo"}
                </button>
                <button
                  onClick={() => setShowActivity(!showActivity)}
                  className={`btn-secondary w-full sm:w-auto ${showActivity ? "bg-accent-500/20 border-accent-500 text-accent-400" : ""}`}
                >
                  Today's Activity
                </button>
              </div>
            </div>

            {/* Full-width equity chart */}
            <div className="mb-6">
              <EquityChart
                data={result.equity}
                timeRange={timeRange}
                onTimeRangeChange={(range) => {
                  setTimeRange(range);
                  setSelectedRange(null);
                }}
                selectedRange={selectedRange}
                onRangeSelect={setSelectedRange}
              />
            </div>

            {/* Monte Carlo Analysis */}
            {monteCarloResult && (
              <div className="mb-6">
                <MonteCarloAnalysis
                  result={monteCarloResult}
                  onClose={() => setMonteCarloResult(null)}
                />
              </div>
            )}

            {/* Range Analysis Panel */}
            {selectedRange && result.tickerReturns && (
              <div className="mb-6">
                <RangeAnalysisPanel
                  equity={result.equity}
                  allocations={result.allocations}
                  tickerReturns={result.tickerReturns}
                  selectedRange={selectedRange}
                  onClear={() => setSelectedRange(null)}
                />
              </div>
            )}

            <div className="mb-6">
              <MetricsGrid
                metrics={result.metrics}
                initialCapital={result.initialCapital}
                finalValue={result.finalValue}
                totalSlippage={result.totalSlippage}
                totalRegulatoryFees={result.totalRegulatoryFees}
              />
            </div>

            {/* Today's Activity Panel */}
            {showActivity && (
              <div className="mb-6 animate-slide-up">
                <TodayActivity allocation={result.todayAllocation} />
              </div>
            )}

            {/* Backtest Data */}
            {result.allocations && result.allocations.length > 0 && (
              <HistoricalAllocations allocations={result.allocations} isAdmin={userIsAdmin} />
            )}
          </div>
        )}

        {/* Empty State */}
        {!result && !isLoading && !progress && (
          <div className="glass-card p-6 sm:p-12 text-center animate-fade-in">
            <div className="text-surface-400 mb-6">
              <div className="w-20 h-20 mx-auto rounded-2xl bg-surface-800/50 flex items-center justify-center mb-4">
                <svg
                  className="w-10 h-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-semibold text-surface-200 mb-3">
              Ready to Backtest
            </h3>
            <p className="text-surface-400 mb-6 max-w-md mx-auto">
              Paste your strategy code above and click "Run Backtest"
              to analyze historical performance
            </p>
            <button onClick={handleLoadSample} className="btn-outline">
              Try Sample Strategy
            </button>
          </div>
        )}
      </div>

      {/* Save Strategy Modal */}
      {result && (
        <SaveStrategyModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          strategyCode={result.strategyCode || strategyCode}
          metrics={result.metrics}
          suggestedName={result.strategyName}
          onSaveSuccess={(savedStrategy) => {
            // Update current strategy ID when saved
            setCurrentStrategyId(savedStrategy.id);
            setStrategyName(savedStrategy.name);
            // Update URL to reflect the saved strategy
            navigate(`/backtest/${savedStrategy.id}`, { replace: true });
          }}
        />
      )}

      {/* Share Strategy Modal */}
      <ShareStrategyModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        strategyId={currentStrategyId || ""}
        strategyName={strategyName}
        metrics={
          result?.metrics
            ? {
                cagr: result.metrics.cagr,
                sharpe: result.metrics.sharpe,
                maxDrawdown: result.metrics.maxDrawdown,
              }
            : undefined
        }
      />

      {/* Invest Modal */}
      <InvestModal
        isOpen={showInvestModal}
        onClose={() => setShowInvestModal(false)}
        strategyName={strategyName}
        onInvest={handleInvest}
      />

      {/* Login Prompt Modal */}
      <LoginPromptModal
        isOpen={!!loginPromptAction}
        onClose={() => setLoginPromptAction(null)}
        action={loginPromptAction || "bookmark"}
      />

      {/* Optimization Config Modal */}
      <OptimizationConfigModal
        isOpen={showOptimizeModal}
        onClose={() => setShowOptimizeModal(false)}
        strategyCode={strategyCode || result?.strategyCode || ""}
        strategyName={strategyName || result?.strategyName || "Strategy"}
        currentMetrics={
          result?.metrics
            ? {
                cagr: result.metrics.cagr,
                sharpe: result.metrics.sharpe,
                max_drawdown: result.metrics.maxDrawdown,
                sortino: result.metrics.sortino,
              }
            : undefined
        }
        onSubmit={(_jobId) => {
          navigate("/optimizer/jobs?submitted=1");
        }}
      />
    </div>
  );
}
